import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

type Variant = 'primary' | 'primaryBlue' | 'violet' | 'ghost' | 'quiet';
type Size = 'md' | 'lg' | 'icon';

const VARIANT: Record<Variant, string> = {
  /** Основное действие шага — источник резервирует мяту строго под эту роль. */
  primary: 'bg-mint font-semibold text-ink active:brightness-95',
  /** CTA в цвет индикатора прогресса онбординга: «Продолжить» перекликается с
   *  полосой заполненности над ней (решение Дмитрия), а не с остальными CTA. */
  primaryBlue: 'bg-signal-blue font-semibold text-white active:brightness-95',
  /** Цвет кнопки микрофона (`MicButton`) — для действия, которое встаёт на её
   *  место («Завершить проверку» в `AssessmentSession`). */
  violet: 'bg-[#7c5cff] font-semibold text-white active:brightness-95',
  /** Вторичный выбор (карточки-варианты) — граница нужна: белая карточка
   *  на почти-белом холсте без неё сливается с фоном. */
  ghost: 'border border-hairline bg-glass text-ink active:bg-glass-2',
  /** Текстовая ссылка («Назад», «Пропустить»). */
  quiet: 'font-medium text-ink-subtle active:text-ink-muted',
};

const SIZE: Record<Size, string> = {
  md: 'min-h-11 px-4 py-2.5 text-[15px]',
  lg: 'min-h-13 px-5 py-3.5 text-base',
  /** Круглая кнопка с одной иконкой — без отступов, размер задаёт место вызова
   *  (`size-12`). Не `p-0` поверх `md`: `cn` конфликты не снимает, `px-4`
   *  оставался и сплющивал иконку в кнопке 48 px до 14 px по ширине. */
  icon: '',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Тип задаём явно: внутри <form> дефолтный submit перезагрузил бы мини-апп.
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[45px] transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-signal-blue/40',
        'disabled:pointer-events-none disabled:opacity-35',
        VARIANT[variant],
        variant !== 'quiet' && SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
